import {
    isValidElement,
    type ReactNode,
    cloneElement,
    Children,
    Fragment
} from "react";
import {
    type TextProps,
    Platform
} from "react-native";
import type {
    CardLayoutConstantType,
    CardHiddenConstantType,
    CardPartFlagProps,
    CardPartLayout,
    CardPartInfo,
    CardPartKind,
    CardLayout
} from "./type";

export const CARD_PART_KINDS: Array<CardPartKind> = [
    "description",
    "content",
    "header",
    "action",
    "footer",
    "title"
];

export const CARD_DEFAULT_LAYOUT: CardPartLayout = {
    isDividedHeaderBefore: false,
    isDividedFooterAfter: false,
    isFirst: false,
    isLast: false
};

const isCardPartKind = (value: unknown): value is CardPartKind => {
    return typeof value === "string" && CARD_PART_KINDS.some((kind) => kind === value);
};

export const flattenCardChildren = (children: ReactNode, prefix: string = ""): Array<ReactNode> => {
    const nodes: Array<ReactNode> = [];

    Children.toArray(children).forEach((child, index) => {
        if(isValidElement<CardPartFlagProps>(child) && child.type === Fragment) {
            nodes.push(...flattenCardChildren(child.props.children, `${prefix}${index}.`));

            return;
        }

        if(!prefix || !isValidElement(child)) {
            nodes.push(child);

            return;
        }

        nodes.push(cloneElement(child, {
            key: `${prefix}${child.key ?? index}`
        }));
    });

    return nodes;
};

export const getCardHiddenProps = ({
    isPressable,
    text
}: CardHiddenConstantType): Pick<TextProps, "accessibilityElementsHidden" | "importantForAccessibility"> => {
    if(Platform.OS === "web" || !isPressable || !text) {
        return {};
    }

    return {
        accessibilityElementsHidden: true,
        importantForAccessibility: "no"
    };
};

export const getCardChildrenText = (children: ReactNode): string | undefined => {
    const nodes = Children.toArray(children);

    if(!nodes.length || !nodes.every((node) => typeof node === "string" || typeof node === "number")) {
        return undefined;
    }

    return nodes.join("");
};

export const getCardPartKind = (node: ReactNode): CardPartKind | undefined => {
    if(!isValidElement(node)) {
        return undefined;
    }

    const elementType: unknown = node.type;

    if(typeof elementType !== "function" || !("cardPart" in elementType)) {
        return undefined;
    }

    return isCardPartKind(elementType.cardPart) ? elementType.cardPart : undefined;
};

export const getCardPartInfo = (node: ReactNode): CardPartInfo | undefined => {
    const kind = getCardPartKind(node);

    if(!kind || !isValidElement<CardPartFlagProps>(node)) {
        return undefined;
    }

    return {
        isDivided: kind === "footer" ? node.props.isDivided !== false : node.props.isDivided === true,
        isBand: kind === "footer" && node.props.variant === "band",
        isFlush: kind === "content" && node.props.isFlush === true,
        kind
    };
};

export const getCardLayout = ({
    children
}: CardLayoutConstantType): CardLayout => {
    const nodes = flattenCardChildren(children);

    const infos = nodes.map(getCardPartInfo);

    const lastIndex = nodes.length - 1;

    const firstInfo = infos[0];

    const lastInfo = infos[lastIndex];

    return {
        isLastFlush: !!lastInfo && (lastInfo.kind === "content" && lastInfo.isFlush || lastInfo.kind === "footer" && lastInfo.isBand),
        items: nodes.map((node, index) => {
            const previousInfo = infos[index - 1];

            const nextInfo = infos[index + 1];

            return {
                layout: {
                    isDividedHeaderBefore: previousInfo?.kind === "header" && previousInfo.isDivided,
                    isDividedFooterAfter: nextInfo?.kind === "footer" && nextInfo.isDivided,
                    isLast: index === lastIndex,
                    isFirst: index === 0
                },
                node
            };
        }),
        isFirstFlush: !!firstInfo && firstInfo.kind === "content" && firstInfo.isFlush
    };
};
